/**
 * Các hàm tiện ích xử lý luồng hình ảnh (DisplayMedia), nhận diện thiết bị Mobile/Desktop,
 * MIME type và trích xuất Thumbnail cho tính năng Ghi hình tương tác AI & Toàn trang web.
 */

export function isMobileRecordingDevice(): boolean {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const isMobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua);
    const isSmallTouchViewport = window.innerWidth <= 900 && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
    return isMobileUA || isSmallTouchViewport;
}

/**
 * Kiểm tra xem có thể dùng chế độ quay toàn Tab trình duyệt (getDisplayMedia) trên Máy tính hay không.
 * Nếu trả về false (ví dụ trên Điện thoại iOS Safari / Android Chrome), hệ thống sẽ tự động chuyển sang
 * chế độ Mobile 360 Canvas Compositor (captureStream).
 */
export function canUseDesktopDisplayMedia(): boolean {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    if (isMobileRecordingDevice()) return false;
    return typeof navigator.mediaDevices?.getDisplayMedia === 'function';
}

/**
 * Giữ lại alias tương thích ngược cho các module/test kiểm tra thiết bị di động.
 */
export function isMobileRecordingUnsupported(): boolean {
    return !canUseDesktopDisplayMedia();
}

export function getSupportedWebRecordingMimeType(): string {
    if (typeof MediaRecorder === 'undefined') return '';
    const candidates = [
        'video/mp4;codecs=avc1,mp4a.40.2',
        'video/mp4;codecs=avc1,opus',
        'video/mp4',
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=h264,opus',
        'video/webm'
    ];
    for (const mime of candidates) {
        if (MediaRecorder.isTypeSupported(mime)) return mime;
    }
    return '';
}

export function resolveDefaultWebRecordingTitle(): string {
    if (typeof window === 'undefined') return 'Tương tác AI';
    const sceneTitle =
        (window as any)?.currentSceneInfo?.title ||
        (window as any)?.currentSceneInfo?.name ||
        document.title?.split('|')?.[0]?.trim() ||
        'Tour 360';
    return `Tương tác AI - ${sceneTitle}`.slice(0, 120);
}

export async function requestBrowserDisplayStream(): Promise<MediaStream> {
    const displayConstraints: any = {
        video: {
            displaySurface: 'browser',
            frameRate: { ideal: 30, max: 30 },
            width: { ideal: 1920, max: 1920 },
            height: { ideal: 1080, max: 1080 }
        },
        audio: true,
        preferCurrentTab: true,
        selfBrowserSurface: 'include',
        surfaceSwitching: 'include',
        systemAudio: 'include'
    };
    return navigator.mediaDevices.getDisplayMedia(displayConstraints);
}

export function createHiddenPreviewVideo(videoTrack: MediaStreamTrack): HTMLVideoElement | null {
    if (typeof document === 'undefined') return null;
    const videoEl = document.createElement('video');
    videoEl.muted = true;
    videoEl.playsInline = true;
    videoEl.srcObject = new MediaStream([videoTrack]);
    videoEl.play().catch(() => {});
    return videoEl;
}

export async function captureStreamThumbnailBlob(previewVideoEl: HTMLVideoElement | null): Promise<Blob | null> {
    if (!previewVideoEl || typeof document === 'undefined') return null;
    try {
        const canvas = document.createElement('canvas');
        canvas.width = 1920;
        canvas.height = 1080;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.fillStyle = '#0f172a';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(previewVideoEl, 0, 0, canvas.width, canvas.height);
        return await new Promise<Blob | null>((resolve) => {
            try {
                canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.85);
            } catch {
                resolve(null);
            }
        });
    } catch {
        return null;
    }
}
